/**
 * Micro-interactions for UI Components
 *
 * Reusable Framer Motion configurations for button presses, hovers,
 * and other small but delightful interactions.
 */

import { type Transition, type TargetAndTransition } from 'framer-motion';

/**
 * Button press animation
 * Scales down on tap for clear tactile feedback on mobile
 */
export const buttonTapAnimation: TargetAndTransition = {
  scale: 0.90,
};

/**
 * Button hover animation
 * Slightly scales up and lifts on hover
 */
export const buttonHoverAnimation: TargetAndTransition = {
  scale: 1.02,
  y: -2,
};

/**
 * Icon button press animation
 * More pronounced scale for small touch targets
 */
export const iconButtonTapAnimation: TargetAndTransition = {
  scale: 0.9,
};

/**
 * Icon button hover animation
 * Rotate and scale for playful effect
 */
export const iconButtonHoverAnimation: TargetAndTransition = {
  scale: 1.1,
  rotate: [0, -5, 5, 0],
};

/**
 * Card hover animation
 * Subtle lift and shadow increase
 */
export const cardHoverAnimation: TargetAndTransition = {
  y: -4,
  scale: 1.01,
};

/**
 * Spring transition for buttons
 * Snappy and responsive feel
 */
export const buttonSpringTransition: Transition = {
  type: 'spring',
  stiffness: 400,
  damping: 17,
};

/**
 * Smooth transition for cards
 * More fluid motion for larger elements
 */
export const cardTransition: Transition = {
  type: 'spring',
  stiffness: 300,
  damping: 20,
};

/**
 * Fast transition for icon buttons
 * Quick response for small interactions
 */
export const fastSpringTransition: Transition = {
  type: 'spring',
  stiffness: 500,
  damping: 15,
};

/**
 * Bounce transition
 * Playful bounce effect
 */
export const bounceTransition: Transition = {
  type: 'spring',
  stiffness: 300,
  damping: 10,
};

/**
 * Input focus animation
 * Gentle scale and glow effect
 */
export const inputFocusAnimation: TargetAndTransition = {
  scale: 1.01,
};

/**
 * Checkbox/Radio animation
 * Quick snap effect
 */
export const checkboxAnimation: TargetAndTransition = {
  scale: [1, 1.2, 1],
};

/**
 * Switch toggle animation
 * Smooth slide effect
 */
export const switchTransition: Transition = {
  type: 'spring',
  stiffness: 700,
  damping: 30,
};

/**
 * Tooltip animation
 * Quick fade and slight movement
 */
export const tooltipAnimation = {
  initial: { opacity: 0, y: 5 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 5 },
  transition: { duration: 0.15 },
};

/**
 * Dropdown menu animation
 * Slides down and fades in
 */
export const dropdownAnimation = {
  initial: { opacity: 0, y: -10, scale: 0.95 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -10, scale: 0.95 },
  transition: { duration: 0.2 },
};

/**
 * Modal backdrop animation
 * Fade in/out
 */
export const backdropAnimation = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.2 },
};

/**
 * Modal content animation
 * Scales up and fades in
 */
export const modalAnimation = {
  initial: { opacity: 0, scale: 0.95, y: 20 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: 20 },
  transition: {
    type: 'spring',
    stiffness: 300,
    damping: 25,
  },
};

/**
 * Loading spinner animation
 * Continuous rotation
 */
export const spinnerAnimation = {
  rotate: 360,
  transition: {
    duration: 1,
    repeat: Infinity,
    ease: 'linear',
  },
};

/**
 * Pulse animation
 * For attention-grabbing elements
 */
export const pulseAnimation = {
  scale: [1, 1.05, 1],
  transition: {
    duration: 2,
    repeat: Infinity,
    ease: 'easeInOut',
  },
};

/**
 * Shake animation
 * For error states or validation failures
 */
export const shakeAnimation = {
  x: [0, -10, 10, -10, 10, 0],
  transition: {
    duration: 0.5,
  },
};

/**
 * Success checkmark animation
 * Draw-in effect for checkmarks
 */
export const checkmarkAnimation = {
  pathLength: [0, 1],
  transition: {
    duration: 0.3,
    ease: 'easeOut',
  },
};
